import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import {
    Badge,
    Box,
    Flex,
    HStack,
    IconButton,
    Spacer,
    Text,
    Tooltip,
    useColorModeValue,
    useDisclosure,
    VStack,
} from '@chakra-ui/react';
import { translate } from '../../../i18n';
import {
    GameAdminCommentDto,
} from '../../../models/game/dto/game-admin-comment.dto';
import { useAuth } from '../../auth/AuthProvider';
import { useDeleteGameAdminComment } from '../hooks/useGameAdminComments';
import { categoryColors, categoryLabels } from './adminCommentConstants';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import EditAdminCommentModal from './EditAdminCommentModal';

interface AdminCommentCardProps {
    comment: GameAdminCommentDto;
    gameId: number;
}

export default function AdminCommentCard({ comment, gameId }: AdminCommentCardProps) {
    const {
        isOpen: isEditOpen,
        onOpen: onEditOpen,
        onClose: onEditClose,
    } = useDisclosure();

    const {
        isOpen: isDeleteOpen,
        onOpen: onDeleteOpen,
        onClose: onDeleteClose,
    } = useDisclosure();

    const deleteCommentMutation = useDeleteGameAdminComment(gameId);

    // The API allows only the AUTHOR to edit or delete a comment
    // (`game.controller.ts` → updateAdminComment / deleteAdminComment), so the
    // icons on everyone else's comments were actions the server refuses. The
    // comparison is on email because that is the identity the API compares
    // (`comment.author.email !== adminEmail`); it is case-normalised here
    // because the claim's casing is not guaranteed to match the stored member
    // row's, and getting it wrong only ever HIDES a button the user may use.
    const { currentUser } = useAuth();
    const myEmail = typeof currentUser?.email === 'string' ? currentUser.email : undefined;
    const isAuthor = Boolean(myEmail)
        && myEmail.toLowerCase() === comment.author?.email?.toLowerCase();

    const handleDelete = () => {
        deleteCommentMutation.mutate(comment.id);
        onDeleteClose();
    };

    const cardBg = useColorModeValue('white', 'gray.700');

    const formatDate = (dateString: string) => {
        const d = new Date(dateString);
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = months[d.getMonth()];
        const day = d.getDate().toString().padStart(2, '0');
        const year = d.getFullYear();
        const hours = d.getHours().toString().padStart(2, '0');
        const minutes = d.getMinutes().toString().padStart(2, '0');
        return `${month} ${day}, ${year} - ${hours}:${minutes}`;
    };

    const getInitials = (email: string) => {
        return email.split('@')[0].substring(0, 2).toUpperCase();
    };

    return (
        <Box
            borderWidth="1px"
            borderRadius="lg"
            p={4}
            bg={cardBg}
            shadow="sm"
            _hover={{ shadow: 'md' }}
            transition="shadow 0.2s"
        >
            <VStack align="stretch" spacing={3}>
                {/* Header with category, author, and actions */}
                <Flex align="center">
                    <HStack spacing={2}>
                        {comment.category && (
                            <Badge colorScheme={categoryColors[comment.category]} size="sm">
                                {categoryLabels[comment.category]}
                            </Badge>
                        )}

                        <HStack spacing={2}>
                            <Box
                                w={6}
                                h={6}
                                bg="brand.500"
                                color="white"
                                borderRadius="full"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                fontSize="xs"
                                fontWeight="bold"
                            >
                                {getInitials(comment.author.email)}
                            </Box>
                            <Text fontSize="sm" color="gray.600">
                                {comment.author.email}
                            </Text>
                            <Badge size="xs" colorScheme="gray">
                                {comment.author.role}
                            </Badge>
                        </HStack>
                    </HStack>

                    <Spacer />

                    <HStack spacing={1}>
                        {isAuthor && <>
                        <Tooltip label={translate('admin_comments.edit')}>
                            <IconButton
                                icon={<EditIcon />}
                                size="sm"
                                variant="ghost"
                                colorScheme="brand"
                                onClick={onEditOpen}
                                aria-label={translate('admin_comments.edit')}
                            />
                        </Tooltip>

                        <Tooltip label={translate('admin_comments.delete')}>
                            <IconButton
                                icon={<DeleteIcon />}
                                size="sm"
                                variant="ghost"
                                colorScheme="red"
                                onClick={onDeleteOpen}
                                aria-label={translate('admin_comments.delete')}
                            />
                        </Tooltip>
                        </>}
                    </HStack>
                </Flex>

                {/* Comment content */}
                <Text fontSize="md" lineHeight="1.5">
                    {comment.content}
                </Text>

                {/* Footer with timestamps */}
                <HStack justify="space-between" fontSize="xs" color="gray.500">
                    <Text>
                        {translate('admin_comments.created_at')}: {formatDate(comment.createdAt)}
                    </Text>
                    {comment.updatedAt !== comment.createdAt && (
                        <Text>
                            {translate('admin_comments.updated_at')}: {formatDate(comment.updatedAt)}
                        </Text>
                    )}
                </HStack>
            </VStack>

            {/* Edit Modal */}
            <EditAdminCommentModal
                isOpen={isEditOpen}
                onClose={onEditClose}
                comment={comment}
                gameId={gameId}
            />

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
                isOpen={isDeleteOpen}
                onClose={onDeleteClose}
                onConfirm={handleDelete}
                isLoading={deleteCommentMutation.isLoading}
                commentContent={comment.content}
            />
        </Box>
    );
}
